import React, { useCallback, useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import {
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	IconButton,
	Chip,
	Tooltip,
	LinearProgress,
	Select,
	MenuItem,
	TextField,
} from "@mui/material";
import {
	mdiRefresh,
	mdiMagnify,
	mdiClose,
	mdiDatabaseRefreshOutline,
	mdiPlusCircleOutline,
	mdiArrowUpBold,
	mdiArrowDownBold,
	mdiAlertCircleOutline,
	mdiCheckCircleOutline,
} from "@mdi/js";
import { clsx } from "@ismael1361/utils";
import { HandleError } from "shared/utils";
import { NotistackServices, WorkspaceServices } from "shared/services";
import { BackofficeAdminServices, MemberConsumptionDTO } from "shared/services/BackofficeAdminServices";

import styles from "./styles.module.scss";

type SeniorityLevel = "junior" | "pleno" | "senior";

const SENIORITY_LABEL: Record<SeniorityLevel, string> = {
	junior: "Júnior",
	pleno: "Pleno",
	senior: "Sênior",
};

const SENIORITY_ORDER: SeniorityLevel[] = ["junior", "pleno", "senior"];

function formatTokens(n: number): string {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(".0", "")}M`;
	if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
	return String(n);
}

export const ConsumptionPage: React.FC = () => {
	const [members, setMembers] = useState<MemberConsumptionDTO[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState<"ALL" | "active" | "inactive" | "limit_reached">("ALL");
	const [seniorityFilter, setSeniorityFilter] = useState<"ALL" | SeniorityLevel>("ALL");

	// Reset tokens dialog
	const [resetTarget, setResetTarget] = useState<MemberConsumptionDTO | null>(null);

	// Add tokens dialog
	const [addTokensTarget, setAddTokensTarget] = useState<MemberConsumptionDTO | null>(null);
	const [tokenInput, setTokenInput] = useState("");

	// Seniority change dialog
	const [seniorityTarget, setSeniorityTarget] = useState<MemberConsumptionDTO | null>(null);
	const [newSeniority, setNewSeniority] = useState<SeniorityLevel>("pleno");

	const time = useRef<NodeJS.Timeout>(undefined);

	const filtered = React.useMemo(() => {
		return members.filter((m) => {
			if (statusFilter === "active" && !m.isActive) return false;
			if (statusFilter === "inactive" && m.isActive) return false;
			if (statusFilter === "limit_reached" && !m.tokenLimitReached) return false;
			if (seniorityFilter !== "ALL" && m.seniorityLevel !== seniorityFilter) return false;
			if (searchQuery) {
				const q = searchQuery.toLowerCase();
				return (
					m.memberName.toLowerCase().includes(q) ||
					m.workspaceName?.toLowerCase().includes(q) ||
					m.teamName?.toLowerCase().includes(q) ||
					m.memberLibraryName?.toLowerCase().includes(q)
				);
			}
			return true;
		});
	}, [members, statusFilter, seniorityFilter, searchQuery]);

	const load = useCallback(() => {
		setLoading(true);
		clearTimeout(time.current);
		time.current = setTimeout(async () => {
			try {
				const data = await BackofficeAdminServices.listMembersConsumption();
				setMembers(data);
			} catch (error) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar dados de consumo");
			} finally {
				setLoading(false);
			}
		}, 300);
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const handleReset = async () => {
		if (!resetTarget) return;
		setSaving(true);
		try {
			await BackofficeAdminServices.resetMemberTokens(resetTarget.memberId);
			NotistackServices.showSuccessMessage(`Tokens de ${resetTarget.memberName} resetados.`);
			setResetTarget(null);
			load();
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao resetar tokens.");
		} finally {
			setSaving(false);
		}
	};

	const handleAddTokens = async () => {
		if (!addTokensTarget) return;
		const tokens = parseInt(tokenInput, 10);
		if (!Number.isInteger(tokens) || tokens <= 0) {
			NotistackServices.showErrorMessage("Informe um número inteiro positivo.");
			return;
		}
		if (!addTokensTarget.workspaceId) {
			NotistackServices.showErrorMessage("Workspace não identificada para este membro.");
			return;
		}
		setSaving(true);
		try {
			await WorkspaceServices.addMemberTokens(
				addTokensTarget.workspaceId,
				addTokensTarget.teamId,
				addTokensTarget.memberId,
				tokens,
			);
			NotistackServices.showSuccessMessage(`${formatTokens(tokens)} tokens adicionados a ${addTokensTarget.memberName}.`);
			setAddTokensTarget(null);
			setTokenInput("");
			load();
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao adicionar tokens.");
		} finally {
			setSaving(false);
		}
	};

	const openSeniorityDialog = (m: MemberConsumptionDTO) => {
		setSeniorityTarget(m);
		setNewSeniority(m.seniorityLevel as SeniorityLevel);
	};

	const handleChangeSeniority = async () => {
		if (!seniorityTarget || !seniorityTarget.workspaceId) return;
		if (newSeniority === seniorityTarget.seniorityLevel) {
			setSeniorityTarget(null);
			return;
		}
		setSaving(true);
		try {
			await WorkspaceServices.updateMember(seniorityTarget.workspaceId, seniorityTarget.teamId, seniorityTarget.memberId, {
				seniority_level: newSeniority,
			});
			const label = SENIORITY_LABEL[newSeniority];
			NotistackServices.showSuccessMessage(`${seniorityTarget.memberName} → ${label}`);
			setSeniorityTarget(null);
			load();
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao alterar nível.");
		} finally {
			setSaving(false);
		}
	};

	// Summary stats
	const stats = React.useMemo(() => {
		const active = members.filter((m) => m.isActive);
		const limitReached = active.filter((m) => m.tokenLimitReached);
		const totalUsed = active.reduce((sum, m) => sum + m.tokensUsed, 0);
		const avgPct = active.length > 0 ? Math.round(active.reduce((sum, m) => sum + (m.usagePct ?? 0), 0) / active.length) : 0;
		return { total: members.length, active: active.length, limitReached: limitReached.length, totalUsed, avgPct };
	}, [members]);

	return (
		<>
			<div className={styles.container}>
				{/* Header */}
				<div className={styles.pageHeader}>
					<div>
						<h2>Dashboard de Consumo</h2>
						<p>Monitore o uso de tokens de todos os membros na plataforma.</p>
					</div>
					<div className={styles.headerActions}>
						<button className={clsx(styles.btn, styles.btnSecondary)} onClick={load} disabled={loading}>
							<Icon path={mdiRefresh} size={0.7} />
							Recarregar
						</button>
					</div>
				</div>

				{/* Summary Cards */}
				{!loading && (
					<div className={styles.summaryRow}>
						<div className={styles.summaryCard}>
							<span className={styles.summaryValue}>{stats.active}</span>
							<span className={styles.summaryLabel}>Membros ativos</span>
						</div>
						<div className={clsx(styles.summaryCard, stats.limitReached > 0 && styles.summaryCardAlert)}>
							<span className={styles.summaryValue}>{stats.limitReached}</span>
							<span className={styles.summaryLabel}>Limite atingido</span>
						</div>
						<div className={styles.summaryCard}>
							<span className={styles.summaryValue}>{formatTokens(stats.totalUsed)}</span>
							<span className={styles.summaryLabel}>Tokens consumidos</span>
						</div>
						<div className={styles.summaryCard}>
							<span className={styles.summaryValue}>{stats.avgPct}%</span>
							<span className={styles.summaryLabel}>Uso médio</span>
						</div>
					</div>
				)}

				{/* Filters */}
				<div className={styles.filtersToolbar}>
					<div className={styles.filterGroup}>
						<span className={styles.label}>Status:</span>
						<Select className={styles.select} variant="standard" disableUnderline value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} disabled={loading}>
							<MenuItem value="ALL">Todos</MenuItem>
							<MenuItem value="active">Ativos</MenuItem>
							<MenuItem value="inactive">Inativos</MenuItem>
							<MenuItem value="limit_reached">Limite atingido</MenuItem>
						</Select>
					</div>

					<div className={styles.filterGroup}>
						<span className={styles.label}>Nível:</span>
						<Select className={styles.select} variant="standard" disableUnderline value={seniorityFilter} onChange={(e) => setSeniorityFilter(e.target.value as any)} disabled={loading}>
							<MenuItem value="ALL">Todos</MenuItem>
							{SENIORITY_ORDER.map((lvl) => (
								<MenuItem key={lvl} value={lvl}>{SENIORITY_LABEL[lvl]}</MenuItem>
							))}
						</Select>
					</div>

					<div className={styles.searchGroup}>
						<Icon path={mdiMagnify} size={0.65} className={styles.searchIcon} />
						<input
							className={styles.searchInput}
							placeholder="Buscar por membro, workspace, team..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							disabled={loading}
						/>
					</div>

					<div className={styles.templateCount}>
						<span>{filtered.length} membro{filtered.length !== 1 && "s"}</span>
					</div>
				</div>

				{/* Content */}
				<div className={styles.content}>
					{loading ? (
						<div className={styles.loadingState}>
							<CircularProgress color="inherit" size={24} />
							<span>Carregando dados de consumo...</span>
						</div>
					) : filtered.length === 0 ? (
						<div className={styles.emptyState}>
							<Icon path={mdiDatabaseRefreshOutline} size={2} />
							<p>Nenhum membro encontrado.</p>
						</div>
					) : (
						<div className={styles.tableWrapper}>
							<table className={styles.table}>
								<thead>
									<tr>
										<th>Membro</th>
										<th>Workspace / Team</th>
										<th>Nível</th>
										<th>Consumo</th>
										<th>Tokens</th>
										<th>Rate Limit</th>
										<th>Status</th>
										<th>Ações</th>
									</tr>
								</thead>
								<tbody>
									{filtered.map((m) => {
										const pct = m.usagePct ?? 0;
										const isNear = pct >= 80;
										const barColor: "error" | "warning" | "primary" = m.tokenLimitReached ? "error" : isNear ? "warning" : "primary";
										const seniority = m.seniorityLevel as SeniorityLevel;
										const currentIdx = SENIORITY_ORDER.indexOf(seniority);
										const canPromote = currentIdx < SENIORITY_ORDER.length - 1;
										const canDemote = currentIdx > 0;

										return (
											<tr key={m.memberId} className={clsx(!m.isActive && styles.rowInactive, m.tokenLimitReached && styles.rowLimitReached)}>
												{/* Membro */}
												<td>
													<div className={styles.memberCell}>
														<div className={styles.memberAvatar}>
															{m.memberName.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(c => c[0].toUpperCase()).join("")}
														</div>
														<div>
															<div className={styles.memberName}>{m.memberName}</div>
															{m.memberLibraryName && (
																<div className={styles.memberRole}>{m.memberLibraryName}</div>
															)}
														</div>
													</div>
												</td>

												{/* Workspace / Team */}
												<td>
													<div className={styles.contextCell}>
														<span className={styles.workspaceName}>{m.workspaceName ?? "—"}</span>
														<span className={styles.teamName}>{m.teamName ?? "—"}</span>
													</div>
												</td>

												{/* Nível */}
												<td>
													<Chip
														label={SENIORITY_LABEL[seniority] ?? seniority}
														size="small"
														variant="outlined"
														color={seniority === "senior" ? "primary" : seniority === "pleno" ? "default" : "default"}
													/>
												</td>

												{/* Consumo (barra) */}
												<td className={styles.progressCell}>
													{m.effectiveLimit !== null ? (
														<>
															<div className={styles.progressLabel}>
																<span>{pct}%</span>
																{m.tokenLimitReached && (
																	<Icon path={mdiAlertCircleOutline} size={0.6} style={{ color: "var(--mui-palette-error-main)" }} />
																)}
															</div>
															<LinearProgress
																variant="determinate"
																value={pct}
																color={barColor}
																className={styles.progressBar}
															/>
														</>
													) : (
														<span className={styles.noLimit}>Sem limite</span>
													)}
												</td>

												{/* Tokens */}
												<td className={styles.tokensCell}>
													<div>{formatTokens(m.tokensUsed)} usado</div>
													{m.tokensBonus > 0 && (
														<div className={styles.tokensBonus}>+{formatTokens(m.tokensBonus)} bônus</div>
													)}
													{m.effectiveLimit !== null && (
														<div className={styles.tokensLimit}>/ {formatTokens(m.effectiveLimit)} limite</div>
													)}
												</td>

												{/* Rate Limit (req/min) */}
												<td className={styles.tokensCell}>
													<div>
														{m.rateLimitCurrent}/{m.rateLimitPerMinute}
														<span className={styles.tokensLimit}> req/min</span>
													</div>
													{m.rateLimitOverride != null && (
														<div className={styles.tokensBonus}>override: {m.rateLimitOverride}</div>
													)}
												</td>

												{/* Status */}
												<td>
													<Chip
														label={!m.isActive ? "Inativo" : m.tokenLimitReached ? "Limite" : "Ativo"}
														size="small"
														color={!m.isActive ? "default" : m.tokenLimitReached ? "error" : "success"}
														variant={!m.isActive ? "outlined" : "filled"}
													/>
												</td>

												{/* Ações */}
												<td>
													<div className={styles.actionsCell}>
														<Tooltip title="Resetar tokens usados">
															<IconButton size="small" onClick={() => setResetTarget(m)} disabled={m.tokensUsed === 0}>
																<Icon path={mdiDatabaseRefreshOutline} size={0.7} />
															</IconButton>
														</Tooltip>
														<Tooltip title="Adicionar tokens">
															<IconButton size="small" onClick={() => { setAddTokensTarget(m); setTokenInput(""); }}>
																<Icon path={mdiPlusCircleOutline} size={0.7} />
															</IconButton>
														</Tooltip>
														{canPromote && (
															<Tooltip title={`Promover para ${SENIORITY_LABEL[SENIORITY_ORDER[currentIdx + 1]]}`}>
																<IconButton size="small" onClick={() => openSeniorityDialog(m)}>
																	<Icon path={mdiArrowUpBold} size={0.7} />
																</IconButton>
															</Tooltip>
														)}
														{canDemote && !canPromote && (
															<Tooltip title={`Rebaixar para ${SENIORITY_LABEL[SENIORITY_ORDER[currentIdx - 1]]}`}>
																<IconButton size="small" onClick={() => openSeniorityDialog(m)}>
																	<Icon path={mdiArrowDownBold} size={0.7} />
																</IconButton>
															</Tooltip>
														)}
													</div>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>

			{/* Reset Tokens Dialog */}
			<Dialog open={!!resetTarget} onClose={() => setResetTarget(null)} maxWidth="xs" fullWidth PaperProps={{ className: styles.dialogPaper }}>
				<DialogTitle className={styles.dialogTitle}>
					<span>Resetar Tokens</span>
					<IconButton size="small" onClick={() => setResetTarget(null)}>
						<Icon path={mdiClose} size={0.8} />
					</IconButton>
				</DialogTitle>
				<DialogContent className={styles.dialogContent}>
					<p className={styles.confirmText}>
						Resetar o consumo de <strong>{resetTarget?.memberName}</strong>? O campo <code>tokens_used</code> voltará a zero.
					</p>
					<p className={styles.confirmHint}>Esta ação não remove os bônus de tokens adicionados manualmente.</p>
				</DialogContent>
				<DialogActions className={styles.dialogActions}>
					<Button onClick={() => setResetTarget(null)} disabled={saving}>Cancelar</Button>
					<Button variant="contained" color="warning" onClick={handleReset} disabled={saving}>
						{saving ? <CircularProgress size={18} color="inherit" /> : "Resetar"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Add Tokens Dialog */}
			<Dialog open={!!addTokensTarget} onClose={() => setAddTokensTarget(null)} maxWidth="xs" fullWidth PaperProps={{ className: styles.dialogPaper }}>
				<DialogTitle className={styles.dialogTitle}>
					<span>Adicionar Tokens — {addTokensTarget?.memberName}</span>
					<IconButton size="small" onClick={() => setAddTokensTarget(null)}>
						<Icon path={mdiClose} size={0.8} />
					</IconButton>
				</DialogTitle>
				<DialogContent className={styles.dialogContent}>
					<p className={styles.dialogHint}>
						Consumido: <strong>{formatTokens(addTokensTarget?.tokensUsed ?? 0)}</strong>
						{" / "}
						Limite: <strong>{addTokensTarget?.effectiveLimit != null ? formatTokens(addTokensTarget.effectiveLimit) : "sem limite"}</strong>
					</p>
					<TextField
						autoFocus
						label="Quantidade de tokens"
						type="number"
						fullWidth
						size="small"
						value={tokenInput}
						onChange={(e) => setTokenInput(e.target.value)}
						inputProps={{ min: 1, step: 10_000 }}
						sx={{ mt: 1 }}
					/>
				</DialogContent>
				<DialogActions className={styles.dialogActions}>
					<Button onClick={() => setAddTokensTarget(null)} disabled={saving}>Cancelar</Button>
					<Button variant="contained" onClick={handleAddTokens} disabled={saving || !tokenInput}>
						{saving ? <CircularProgress size={18} color="inherit" /> : "Confirmar"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Seniority Change Dialog */}
			<Dialog open={!!seniorityTarget} onClose={() => setSeniorityTarget(null)} maxWidth="xs" fullWidth PaperProps={{ className: styles.dialogPaper }}>
				<DialogTitle className={styles.dialogTitle}>
					<span>Alterar Nível — {seniorityTarget?.memberName}</span>
					<IconButton size="small" onClick={() => setSeniorityTarget(null)}>
						<Icon path={mdiClose} size={0.8} />
					</IconButton>
				</DialogTitle>
				<DialogContent className={styles.dialogContent}>
					<p className={styles.dialogHint}>Nível atual: <strong>{SENIORITY_LABEL[seniorityTarget?.seniorityLevel as SeniorityLevel] ?? seniorityTarget?.seniorityLevel}</strong></p>
					<div className={styles.seniorityOptions}>
						{SENIORITY_ORDER.map((lvl) => {
							const currentIdx = SENIORITY_ORDER.indexOf(seniorityTarget?.seniorityLevel as SeniorityLevel ?? "pleno");
							const targetIdx = SENIORITY_ORDER.indexOf(lvl);
							const isPromotion = targetIdx > currentIdx;
							const isCurrent = lvl === seniorityTarget?.seniorityLevel;
							return (
								<button
									key={lvl}
									className={clsx(styles.seniorityOption, newSeniority === lvl && styles.seniorityOptionSelected, isCurrent && styles.seniorityOptionCurrent)}
									onClick={() => setNewSeniority(lvl)}
								>
									<Icon path={isPromotion ? mdiArrowUpBold : isCurrent ? mdiCheckCircleOutline : mdiArrowDownBold} size={0.7} />
									<span>{SENIORITY_LABEL[lvl]}</span>
								</button>
							);
						})}
					</div>
				</DialogContent>
				<DialogActions className={styles.dialogActions}>
					<Button onClick={() => setSeniorityTarget(null)} disabled={saving}>Cancelar</Button>
					<Button variant="contained" onClick={handleChangeSeniority} disabled={saving || newSeniority === seniorityTarget?.seniorityLevel}>
						{saving ? <CircularProgress size={18} color="inherit" /> : "Salvar"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
